import type { PaymentMethodType } from "@lib/types/paymentMethodType";
import type { CustomPaymentMethod, PhysicalPaymentMethods } from "@lib/zod/paymentMethodsSchema";
import { generateUUID } from "@lib/utils/generateUUID";
import {
    CARD_TERMINAL_PAYMENT_TYPE_ID,
    S_API_KEY_PAYMENT_TYPE_ID,
    PAYPAL_PAYMENT_TYPE_ID,
    STRIPE_PAYMENT_TYPE_ID,
    CASH_PAYMENT_TYPE_ID
} from "@lib/consts/paymentIds";

// TODO: get type from db schema not this one below
export interface PaymentMethodsDataInput {
    isStripeEnabled?: boolean | null;
    isPaypalEnabled?: boolean | null;
    isSApiKeyEnabled?: boolean | null;
    defaultPaymentMethods?: PhysicalPaymentMethods | null;
    deliveryPaymentMethods?: PhysicalPaymentMethods | null;
    pickupPaymentMethods?: PhysicalPaymentMethods | null;
}

const NEGATIVE_BALANCE_CHECK_DISABLED_TEMP = true;

export const listAvailablePaymentMethods = (
    paymentMethodsData: PaymentMethodsDataInput | null | undefined,
    currentOrderType: string,
    hasNegativeBalance: boolean = false,
) => {
    let paymentMethods: PhysicalPaymentMethods | null = null;
    const isStripeEnabled = paymentMethodsData?.isStripeEnabled || false;
    const isPaypalEnabled = paymentMethodsData?.isPaypalEnabled || false;
    const isSApiKeyEnabled = paymentMethodsData?.isSApiKeyEnabled || false;
    const defaultMethods = paymentMethodsData?.defaultPaymentMethods ?? null;
    if (paymentMethodsData) {
        if (currentOrderType === 'delivery') {
            paymentMethods = paymentMethodsData.deliveryPaymentMethods?.isActive ?
                paymentMethodsData.deliveryPaymentMethods || null :
                defaultMethods;
        } else if (currentOrderType === 'pickup') {
            paymentMethods = paymentMethodsData.pickupPaymentMethods?.isActive ?
                paymentMethodsData.pickupPaymentMethods || null :
                defaultMethods;
        } else {
            paymentMethods = defaultMethods;
        }
    }

    const methods: PaymentMethodType[] = [];

    if (isStripeEnabled) {
        // keep stripe as first option
        methods.push({
            isOnline: true,
            id: STRIPE_PAYMENT_TYPE_ID,
            name: 'Stripe',
            description: 'Credit/Debit Card, Apple Pay, Google Pay, Link...',
            icon: 'CreditCard'
        });
    }

    if (NEGATIVE_BALANCE_CHECK_DISABLED_TEMP || hasNegativeBalance === false) {
        if (NEGATIVE_BALANCE_CHECK_DISABLED_TEMP) {
            console.log('NEGATIVE_BALANCE_CHECK_DISABLED_TEMP: balance check: disabled temporarily for now');
        }
        if (isSApiKeyEnabled) {
            methods.push({
                isOnline: true,
                id: S_API_KEY_PAYMENT_TYPE_ID,
                name: 'Stripe.', // direct Strpe Api Key
                description: 'Credit/Debit Card, Apple Pay, Google Pay, Link...',
                icon: 'CreditCard'
            });
        }
        if (paymentMethods?.[CASH_PAYMENT_TYPE_ID]) {
            methods.push({
                isOnline: false,
                id: CASH_PAYMENT_TYPE_ID,
                name: 'Cash',
                description: currentOrderType === 'delivery' ? 'Pay with cash on delivery' : 'Pay with cash at pickup',
                icon: 'Banknote'
            });
        }
        if (paymentMethods?.[CARD_TERMINAL_PAYMENT_TYPE_ID]) {
            methods.push({
                isOnline: false,
                id: CARD_TERMINAL_PAYMENT_TYPE_ID,
                name: 'Card',
                description: currentOrderType === 'delivery' ? 'Pay with card on delivery' : 'Pay with card at pickup',
                icon: 'Calculator'
            });
        }
        if (paymentMethods?.customMethods) {
            paymentMethods.customMethods.forEach((method: CustomPaymentMethod) => {
                if (method.isActive && method.name) {
                    methods.push({
                        isOnline: false,
                        id: method.id || generateUUID(),
                        name: method.name,
                        description: method.description,
                    });
                }
            });
        }
        if (isPaypalEnabled) {
            methods.push({
                isOnline: true,
                id: PAYPAL_PAYMENT_TYPE_ID,
                name: 'Paypal',
                description: '',
                // icon: ''
            });
        }
    }


    return methods;
}
